<template>
  <div id="big-client">
    <table>
      <thead>
        <tr>
          <th>{{ $t('CLIENT') }}</th>
          <th>{{ $t('OVERDUE_CNTRCT') }}</th>
          <th>{{ $t('LATE_DAYS') }}</th>
          <th>{{ $t('STATUS') }}</th>
          <th>{{ $t('OUTSTANDING_AMOUNT') }}</th>
          <th>{{ $t('LOAN_BALANCE') }}</th>
          <th>{{ $t('INTEREST_TOTAL') }}</th>
          <th>{{ $t('OVERDUE_FEE') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr :key="index" v-for="(item, index) in items">
          <td><router-link :to="applyParams(PATHS.CLIENT_DETAIL, item.publicId)">{{ item.nameVi }}</router-link></td>
          <td>{{ item.numberOfDueContracts }}</td>
          <td>{{ getOverdueDays(item.latestDate) }}</td>
          <td>{{ $t(item.lastResult.status.key) }}</td>
          <td>{{ addCommas(item.totalOutstandingAmount) }}</td>
          <td>{{ addCommas(item.totalPrincipalAmount) }}</td>
          <td>{{ addCommas(item.totalInterestAmount) }}</td>
          <td>{{ addCommas(item.totalOverdueAmount) }}</td>
          <td><setting :client="item"/></td>
        </tr>
      </tbody>
    </table>
    <pagination
      class="pagination"
      :page="pageFilter.page"
      :pageSize="pageFilter.pageSize"
      :pageSizeOptions="PAGE_SIZE_OPTIONS"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import Pagination from '@/components/Pagination';
import moment from 'moment';
import { addCommas, applyParams } from '@/helpers';

import { DATE_FORMAT, PATHS } from '@/consts';

import paymentReminderServices from '@/views/PaymentReminder/PaymentReminderServices';

import Setting from './Setting';

const { useState } = createNamespacedHelpers('paymentReminder');

const PAGE_SIZE_OPTIONS = [5, 10, 20];

export default {
  name: 'BigClient',
  components: {
    Setting,
    Pagination,
  },
  setup() {
    const { filters, lateDays } = useState(['filters', 'lateDays']);

    const pageFilter = reactive({
      pageSize: 5,
      page: 1
    });

    const items = ref([]);

    const total = ref(0);

    const toDate = computed(() => moment(filters.value.toDate, DATE_FORMAT));

    const getOverdueDays = (dueDateLatest) => {
      const dueDate = moment(dueDateLatest, DATE_FORMAT);
      return toDate.value.diff(dueDate, 'days');
    };

    const fetchData = async () => {
      const filter = {
        ...filters.value,
        ...pageFilter,
      };

      if (lateDays.value === 'TODAY') {
        filter.fromDate = filter.toDate;
      } else if (lateDays.value === '1_DAY') {
        filter.toDate = (moment(filters.value.toDate).subtract(1, 'days')).format(DATE_FORMAT);
        filter.fromDate = filter.toDate;
      } else if (lateDays.value === '2_3_DAYS') {
        filter.toDate = (moment(filters.value.toDate).subtract(2, 'days')).format(DATE_FORMAT);
        filter.fromDate = (moment(filters.value.toDate).subtract(3, 'days')).format(DATE_FORMAT);
      }else if (lateDays.value === '4_5_DAYS') {
        filter.toDate = (moment(filters.value.toDate).subtract(4, 'days')).format(DATE_FORMAT);
        filter.fromDate = (moment(filters.value.toDate).subtract(5, 'days')).format(DATE_FORMAT);
      } else if (lateDays.value === 'OVER_5_DAYS') {
        filter.toDate = (moment(filters.value.toDate).subtract(5, 'days')).format(DATE_FORMAT);
        filter.fromDate = (moment(filters.value.toDate).subtract(1000, 'days')).format(DATE_FORMAT);
      } else {
        filter.minTotalApprovedAmount = 100;
      }
      const { items: itemsResult, total: totalResult } = await paymentReminderServices.getPastDueClients(filter, true, false);
      items.value = itemsResult;
      total.value = totalResult;
    };

    const changePageSize = (value) => {
      pageFilter.pageSize = value;
      pageFilter.page = 1;
      fetchData();
    };

    const changePage = (value) => {
      pageFilter.page = value;
      fetchData();
    };

    onMounted(async () => {
      fetchData();
    });

    watch(filters, () => fetchData());

    watch(lateDays, () => {
      pageFilter.page = 1; // set page default value 1
      fetchData();
    });

    return {
      pageFilter,
      items,
      total,
      changePageSize,
      changePage,
      getOverdueDays,
      PAGE_SIZE_OPTIONS,
      addCommas,
      applyParams,
      PATHS
    };
  },
};
</script>


<style lang='scss' scoped>
@import "~@/assets/scss/colors";

#big-client {
  table {
    width: 100%;
    text-align: center;
    border-collapse: separate;
    border-spacing: 0;

    thead {
      tr {
        text-transform: uppercase;
        font-weight: bold;
        font-family: 'Montserrat';
        padding: 20px 40px;
        font-size: 14px;
        color: $ebonyClay;

        th {
          border-bottom: 0px;
          background-color: $whisper;

          &:first-child {
            border-top-left-radius: 20px;
            border-left: 1px solid $geyser;
          }

          &:last-child {
            border-top-right-radius: 20px;
            border-right: 1px solid $geyser;
          }
        }
      }
    }

    tbody {
      color: $independence;

      tr {
        td {
          padding: 10px 15px;
          transition: all 0.5s ease-in-out;

          a {
            color: inherit;
          }

          &:first-child {
            color: $blueNcs;
            border-left: 1px solid $geyser;
            text-decoration: underline;
            cursor: pointer;
          }

          &:first-child:hover {
            color: $paradisePink;
          }

          &:last-child {
            border-right: 1px solid $geyser;
          }
        }

        &:last-child {
          border-collapse: separate;

          td {
            border-bottom: 1px solid $geyser;

            &:first-child {
              border-bottom-left-radius: 20px;
            }

            &:last-child {
              border-bottom-right-radius: 20px;
            }
          }
        }
      }
    }
  }

  .pagination {
    margin-top: 5px;
  }
}
</style>
